import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { 
  ExclamationCircleIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  DocumentIcon,
  DocumentArrowUpIcon,
  EyeIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const ContextSection = ({ user, organization, toastStyle, darkMode }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingDocuments, setProcessingDocuments] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [envFileError, setEnvFileError] = useState(null);
  
  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Refs
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadMenuRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageLimit, setStorageLimit] = useState(1024 * 1024 * 1024); // Default 1GB

  // Load documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Calculate storage stats
  const calculateStorageUsed = (docs) => {
    const totalBytes = docs.reduce((total, doc) => {
      return total + (doc.fileSize || 0);
    }, 0);
    setStorageUsed(totalBytes);
  };

  // Format bytes to human readable format with precision
  const formatBytesWithPrecision = (bytes, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
  };

  // Get percentage of storage used
  const getStoragePercentage = () => {
    return Math.min(100, Math.round((storageUsed / storageLimit) * 100));
  };

  // Get color for storage bar based on usage
  const getStorageBarColor = () => {
    const percentage = getStoragePercentage();
    if (percentage >= 90) return 'bg-red-500 dark:bg-red-600';
    if (percentage >= 75) return 'bg-yellow-500 dark:bg-yellow-600';
    return 'bg-black dark:bg-white';
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const formattedDocuments = (response.data.documents || []).map(doc => ({
        ...doc,
        id: doc._id || doc.id
      }));
      
      setDocuments(formattedDocuments);
      calculateStorageUsed(formattedDocuments);
      
      // Find documents that are still processing
      const processingDocs = formattedDocuments.filter(doc => doc.status === 'processing');
      if (processingDocs.length > 0) {
        setProcessingDocuments(processingDocs);
      }
      
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents', toastStyle.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      if (!documentId || documentId === 'undefined' || documentId === 'null') {
        console.error('Invalid document ID for deletion:', documentId);
        toast.error('Cannot delete document: Invalid document ID', toastStyle.error);
        return Promise.reject(new Error('Invalid document ID'));
      }
      
      setDeleting(true);
      setDeletingId(documentId);
      
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove document from list
      setDocuments(prevDocs => {
        const updatedDocs = prevDocs.filter(doc => doc.id !== documentId);
        calculateStorageUsed(updatedDocs);
        return updatedDocs;
      });
      
      toast.success('Document deleted successfully', toastStyle.success);
      return Promise.resolve();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document', toastStyle.error);
      return Promise.reject(error);
    } finally {
      setDeleting(false);
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  const getDocumentStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border border-green-200 dark:border-green-700">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-600 dark:text-yellow-100 border border-yellow-200 dark:border-yellow-700">
            <ClockIcon className="w-3 h-3 mr-1" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 border border-red-200 dark:border-red-700">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700">
            Unknown
          </span>
        );
    }
  };

  // Handle document preview
  const handleDocumentPreview = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents/${documentId}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.previewUrl) {
        window.open(response.data.previewUrl, '_blank');
      } else {
        toast.error('Failed to generate preview URL', toastStyle.error);
      }
    } catch (error) {
      console.error('Error getting document preview:', error);
      toast.error('Unable to preview document', toastStyle.error);
    }
  };

  // Get original filename without the prefix
  const getOriginalFilename = (filename) => {
    const match = filename.match(/^\d+-(.+)$/);
    if (match && match[1]) {
      return match[1];
    }
    return filename;
  };

  // Add a function to truncate filenames to a max length
  const truncateFilename = (filename, maxLength = 40) => {
    if (!filename) return '';
    if (filename.length <= maxLength) return filename;
    
    // Get file extension
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension
      return `${filename.substring(0, maxLength)}...`;
    }
    
    const extension = filename.substring(lastDotIndex);
    const nameWithoutExt = filename.substring(0, lastDotIndex);
    
    // Calculate how many characters to keep from the name
    const charsToKeep = maxLength - 3 - extension.length; // 3 for ellipsis
    if (charsToKeep <= 0) {
      // Extension is too long, just truncate the whole thing
      return `${filename.substring(0, maxLength)}...`;
    }
    
    // Return truncated name with extension
    return `${nameWithoutExt.substring(0, charsToKeep)}...${extension}`;
  };

  // Format file type
  const formatFileType = (fileType, filename) => {
    if (!fileType) {
      const extension = filename?.split('.').pop()?.toUpperCase();
      return extension || 'Unknown';
    }
    
    // Special case handling for common formats
    if (fileType.includes('vnd.openxmlformats-officedocument.wordprocessingml.template')) {
      return 'DOTX';
    }
    if (fileType.includes('vnd.openxmlformats-officedocument.wordprocessingml')) {
      return 'DOCX';
    }
    if (fileType.includes('vnd.openxmlformats-officedocument.spreadsheetml')) {
      return 'XLSX';
    }
    if (fileType.includes('vnd.openxmlformats-officedocument.presentationml')) {
      return 'PPTX';
    }
    
    return fileType.replace(/^(application|text|image|audio|video)\//, '').toUpperCase();
  };

  // Filter documents based on search term
  const filteredDocuments = useMemo(() => {
    if (!searchTerm.trim()) return documents;
    
    return documents.filter(doc => {
      const filename = getOriginalFilename(doc.name).toLowerCase();
      const fileType = (doc.fileType || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      return filename.includes(searchLower) || fileType.includes(searchLower);
    });
  }, [documents, searchTerm]);
  
  // Get current page of documents
  const currentDocuments = useMemo(() => {
    const indexOfLastDoc = currentPage * pageSize;
    const indexOfFirstDoc = indexOfLastDoc - pageSize;
    return filteredDocuments.slice(indexOfFirstDoc, indexOfLastDoc);
  }, [filteredDocuments, currentPage, pageSize]);
  
  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredDocuments.length / pageSize);
  }, [filteredDocuments, pageSize]);

  // Render storage usage component
  const renderStorageUsage = () => {
    const percentage = getStoragePercentage();
    const barColor = getStorageBarColor();
    
    return (
      <div className="mb-4 mt-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Storage Used: {formatBytesWithPrecision(storageUsed)} of {formatBytesWithPrecision(storageLimit)}
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
          <div 
            className={`h-2.5 rounded-full ${barColor}`} 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Document List */}
      <div 
        className={`p-4 bg-white dark:bg-[#111111] rounded-2xl border relative border-gray-200 dark:border-[#444444] ${isDragging ? 'border-blue-500 dark:border-blue-400 border-2 ring-2 ring-blue-300 dark:ring-blue-800' : ''}`}
        ref={dropZoneRef}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Document Library</h3>
          <div className="relative">
            <input
              type="file"
              id="documentFile"
              name="documentFile"
              ref={fileInputRef}
              multiple
              className="hidden"
            />
            <button
              disabled={uploadingDoc}
              className={`inline-flex items-center px-3 py-1.5 text-xs border text-sm font-medium rounded-lg shadow-sm 
              ${uploadingDoc
                  ? 'bg-gray-300 dark:bg-[#333333] cursor-not-allowed border-gray-300 dark:border-[#444444] text-gray-500 dark:text-gray-400'
                  : 'bg-black dark:bg-white border-black text-white dark:text-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-200 focus:outline-none'
              }`}
            >
              {uploadingDoc ? (
                <>
                  <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <DocumentArrowUpIcon className="w-3 h-3 mr-1" />
                  Upload Files
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Show storage usage */}
        {renderStorageUsage()}
        
        {/* Search controls */}
        <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-[#121212] focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 dark:border-[#444444] rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-[#121212] py-1 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white"
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="space-y-4">
            <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
              {/* Search and controls skeleton */}
              <div className="flex justify-between items-center gap-2">
                <Skeleton height={36} width={256} />
                <div className="flex items-center gap-2">
                  <Skeleton height={16} width={40} />
                  <Skeleton height={28} width={60} />
                </div>
              </div>
              
              {/* Table skeleton */}
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Header skeleton */}
                  <div className="bg-gray-100 dark:bg-[#1A1A1A] p-3 rounded-t-lg">
                    <div className="flex justify-between">
                      <Skeleton height={16} width={80} />
                      <Skeleton height={16} width={60} />
                      <Skeleton height={16} width={60} />
                      <Skeleton height={16} width={80} />
                    </div>
                  </div>
                  
                  {/* Rows skeleton */}
                  <div className="space-y-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="p-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                          <Skeleton height={20} width={20} />
                          <div className="space-y-1">
                            <Skeleton height={16} width={200} />
                            <Skeleton height={12} width={60} />
                          </div>
                        </div>
                        <Skeleton height={16} width={60} />
                        <Skeleton height={20} width={80} />
                        <div className="flex gap-1">
                          <Skeleton height={32} width={32} />
                          <Skeleton height={32} width={32} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Pagination skeleton */}
              <div className="flex justify-between items-center pt-3">
                <Skeleton height={16} width={150} />
                <div className="flex space-x-1">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} height={28} width={40} />
                  ))}
                </div>
              </div>
            </SkeletonTheme>
          </div>
        ) : filteredDocuments.length === 0 ? (
          searchTerm ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No documents matching "{searchTerm}"
              </p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No documents found. Click Upload Files to get started.
              </p>
            </div>
          ) : null
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-[#1A1A1A]">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Size
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#111111] divide-y divide-gray-200 dark:divide-gray-700">
                {currentDocuments.map((document) => (
                  <tr key={document.id} className="hover:bg-gray-50 dark:hover:bg-[#111111]">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <DocumentIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[250px]" title={getOriginalFilename(document.name)}>
                            {truncateFilename(getOriginalFilename(document.name))}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[150px]" title={formatFileType(document.fileType, document.name)}>
                            {formatFileType(document.fileType, document.name)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{formatFileSize(document.fileSize)}</div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {getDocumentStatusBadge(document.status)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        {/* Preview button */}
                        <button
                          onClick={() => handleDocumentPreview(document.id)}
                          disabled={!document.s3Url || document.status !== 'completed'}
                          title="Preview"
                          className={`flex items-center justify-center p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800
                          ${!document.s3Url || document.status !== 'completed'
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-black hover:text-black dark:text-white dark:hover:text-white'
                          }`}
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        
                        {/* Delete button */}
                        <button
                          onClick={() => handleDeleteDocument(document.id)}
                          disabled={
                            document.status === 'processing' || 
                            (deleting && deletingId === document.id)
                          }
                          title="Delete"
                          className={`flex items-center justify-center p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800
                          ${document.status === 'processing' || (deleting && deletingId === document.id)
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                          }`}
                        >
                          {deleting && deletingId === document.id ? (
                            <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          ) : (
                            <TrashIcon className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center pt-3 pb-1 mt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex-1 text-xs text-gray-500 dark:text-gray-400">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, filteredDocuments.length)} to {Math.min(currentPage * pageSize, filteredDocuments.length)} of {filteredDocuments.length} documents
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className={`px-2 py-1 text-xs rounded-md ${
                      currentPage === 1 
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-2 py-1 text-xs rounded-md ${
                      currentPage === 1 
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    Prev
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-2 py-1 text-xs rounded-md ${
                          currentPage === pageNum
                          ? 'bg-black text-white dark:bg-white dark:text-black font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-2 py-1 text-xs rounded-md ${
                      currentPage === totalPages 
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-2 py-1 text-xs rounded-md ${
                      currentPage === totalPages 
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextSection; 